/**
 * O que mudou em cada versão — lido do `NOVIDADES.md` da aplicação.
 *
 * O arquivo é escrito à mão, junto com a subida de versão (regra no CLAUDE.md), e
 * tem uma forma só:
 *
 *     ## 2.4.0
 *     - Novo: incluir posições de relés em lote, a partir de um .txt.
 *     - Correção: o cadastro de fabricantes voltou a gravar.
 *
 * Uma seção `## X.Y.Z` por versão, a mais nova em cima, e um item por linha começando
 * com `-`. O prefixo `Novo:` / `Correção:` é opcional e vira etiqueta na tela. O resto
 * do arquivo (título, explicação) é ignorado.
 *
 * **Sem `@/` e sem nada do navegador aqui**: o `vite.config.ts` importa este arquivo
 * para gravar as notas no `versao.json`, e lá não há alias nem DOM.
 */

export type TipoDaNovidade = "novo" | "correcao"
export type ItemDaNovidade = { tipo: TipoDaNovidade | null; texto: string }
export type NovidadesDaVersao = { versao: string; itens: ItemDaNovidade[] }

const SECAO = /^##\s+v?(\d+\.\d+\.\d+)\s*$/
const ITEM = /^\s*[-*]\s+(.+?)\s*$/
const TIPO = /^(novo|nova|novidade|correção|correcao|corrigido)\s*:\s*/i

/** As seções do arquivo, na ordem em que estão — a mais nova em cima. */
export function lerNovidades(md: string): NovidadesDaVersao[] {
  const versoes: NovidadesDaVersao[] = []
  let atual: NovidadesDaVersao | null = null

  for (const linha of md.replace(/^﻿/, "").split(/\r\n|\r|\n/)) {
    const secao = SECAO.exec(linha)
    if (secao) {
      atual = { versao: secao[1]!, itens: [] }
      versoes.push(atual)
      continue
    }
    // Outro título (# ou ##) que não é versão encerra a seção.
    if (/^#{1,6}\s/.test(linha)) { atual = null; continue }
    const item = ITEM.exec(linha)
    if (!atual || !item) continue

    const bruto = item[1]!
    const tipo = TIPO.exec(bruto)
    atual.itens.push({
      tipo: !tipo ? null : /^n/i.test(tipo[1]!) ? "novo" : "correcao",
      texto: tipo ? bruto.slice(tipo[0].length) : bruto,
    })
  }
  return versoes
}

/** Compara MAJOR.MINOR.PATCH: negativo se `a` é anterior a `b`. */
export function compararVersoes(a: string, b: string): number {
  const pa = a.split(".").map(Number)
  const pb = b.split(".").map(Number)
  for (let i = 0; i < 3; i++) {
    const d = (pa[i] ?? 0) - (pb[i] ?? 0)
    if (d !== 0) return d
  }
  return 0
}

/** As versões depois de `depoisDe` — o que chegou desde a versão que a aba carregou. */
export function novidadesDesde(todas: readonly NovidadesDaVersao[], depoisDe: string): NovidadesDaVersao[] {
  return todas.filter((n) => compararVersoes(n.versao, depoisDe) > 0)
}

/** A versão atual e as anteriores a ela, até `quantas`. */
export function novidadesAte(todas: readonly NovidadesDaVersao[], versao: string, quantas = 3): NovidadesDaVersao[] {
  return todas.filter((n) => compararVersoes(n.versao, versao) <= 0).slice(0, quantas)
}
